import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Sun, Moon, User, LogOut, Settings, Menu } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import NotificationBell from '../common/NotificationBell';

const Header = ({ onMobileMenuToggle, showMobileMenu }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'ADMIN': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'MODERATOR': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'BUSINESS': return 'bg-violet-500/20 text-violet-400 border-violet-500/30';
      default: return 'bg-green-500/20 text-green-400 border-green-500/30';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'ADMIN': return 'Administrator';
      case 'MODERATOR': return 'Moderator';
      case 'BUSINESS': return 'Biznes';
      default: return 'Përdorues';
    }
  };

  return (
    <header className="h-14 md:h-16 border-b border-border bg-card/50 backdrop-blur-sm flex items-center justify-between px-3 md:px-6 sticky top-0 z-40">
      {/* Left side - Mobile menu button + Page title */}
      <div className="flex items-center gap-2 md:gap-4">
        {/* Mobile Menu Button */}
        {showMobileMenu && (
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden flex-shrink-0"
            onClick={onMobileMenuToggle}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </Button>
        )}
        <h2 className="text-sm md:text-lg font-semibold text-foreground font-outfit truncate">
          AlbMove CSMS
        </h2>
      </div>

      {/* Right side - User info and controls */}
      <div className="flex items-center gap-1.5 md:gap-3">
        {/* Notifications */}
        <NotificationBell />

        {/* Theme Toggle */}
        <Button 
          variant="ghost" 
          size="icon"
          onClick={toggleTheme}
          className="text-muted-foreground hover:text-foreground w-8 h-8 md:w-9 md:h-9"
          data-testid="theme-toggle-btn"
        >
          {isDark ? (
            <Sun className="w-4 h-4 md:w-5 md:h-5" />
          ) : (
            <Moon className="w-4 h-4 md:w-5 md:h-5" />
          )}
        </Button>

        {/* User Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="flex items-center gap-2 md:gap-3 h-auto py-1.5 px-1.5 md:px-3 hover:bg-accent" data-testid="user-menu-btn">
              <div className="w-7 h-7 md:w-9 md:h-9 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                <span className="text-xs md:text-sm font-semibold text-primary">
                  {user?.full_name?.charAt(0) || 'U'}
                </span>
              </div>
              <div className="text-left hidden md:block">
                <p className="text-sm font-medium text-foreground truncate max-w-[120px]">{user?.full_name}</p>
                <p className="text-xs text-muted-foreground truncate max-w-[120px]">{user?.email}</p>
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64 bg-popover border-border">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-2">
                <p className="text-sm font-medium text-foreground">{user?.full_name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
                <span className={`text-xs px-2 py-0.5 rounded-full border w-fit ${getRoleBadgeColor(user?.role)}`}>
                  {getRoleLabel(user?.role)}
                </span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-border" />
            <DropdownMenuItem 
              onClick={() => navigate('/settings')}
              className="cursor-pointer text-foreground hover:bg-accent"
            >
              <Settings className="w-4 h-4 mr-2" />
              Cilësimet
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => navigate('/profile')}
              className="cursor-pointer text-foreground hover:bg-accent"
            >
              <User className="w-4 h-4 mr-2" />
              Profili
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-border" />
            <DropdownMenuItem 
              onClick={handleLogout}
              className="cursor-pointer text-red-400 hover:bg-red-500/10 hover:text-red-400"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Dil nga llogaria
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export default Header;
